import {useState, useEffect, useCallback} from "react";
import {onlineProcessesService} from "../../services";
import {useLoader} from "@quadral/quadral-component-common/dist/components/loader/LoaderContext";
import QuadralList from '@quadral/quadral-component-common/dist/components/list';
import QuadralButton from '@quadral/quadral-component-common/dist/components/button';
import QuadralBadge from "@quadral/quadral-component-common/dist/components/badge"
import QuadralTypography from '@quadral/quadral-component-common/dist/components/typography';
import QuadralContainer from "@quadral/quadral-component-common/dist/components/container";
import UpdatePersonModal from "./UpdatePersonModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faFileInvoice, faXmark } from "@fortawesome/free-solid-svg-icons";

const PersonList = ({participationId, persons, roles, isN3, ...props}) => {
    const {showLoader, hideLoader} = useLoader();

    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [references, setReferences] = useState({});
    const [selectedPerson, setSelectedPerson] = useState([]);

    const getTaxSituation = useCallback(
        (row, householdLabel) => {
            const declarant = [true, 'true', 'oui'].includes(row.taxDeclarant);
            const householdLinkLabel = householdLabel.find(fs => fs.code === row.householdLink)?.label || row.householdLink;
            if (declarant) {
                return("Est autonome fiscalement (a son propre avis d'impôt)");
            } else {
                const person = persons.find(person => person.id === row.mainContactId);
                let fiscallyAttachedPerson;
                if (person) {
                    fiscallyAttachedPerson = `${person.firstname} ${person.lastname}, ${householdLinkLabel}`;
                } else {
                    fiscallyAttachedPerson = "une personne hors foyer";
                }
                return(`Est rattaché fiscalement à ${fiscallyAttachedPerson}`)
            }
        }, [persons]
    );

    useEffect(() => {
        onlineProcessesService.getReferences().then(refs => {
            const familySituations = refs?.PERSON_RECORD_FAMILY_SITUATION || [];
            const occupantType = refs?.PERSON_RECORD_OCCUPANT_TYPE || [];
            const householdLabel = refs?.PERSON_RECORD_HOUSE_HOLD_LINK || [];

            setReferences(refs || {});

            setRows([])
            setRows(
                persons.map(row => ({
                    name: `${row.lastname} ${row.firstname}`,
                    occupantType: occupantType.find(fs => fs.code === row.occupantType)?.label || row.occupantType,
                    taxSituation: getTaxSituation(row, householdLabel),
                    birthdate: row.birthDate,
                    familySituation: familySituations.find(fs => fs.code === row.familySituation)?.label || row.familySituation,
                    supportingDocuments: (
                        <QuadralContainer orientation='row' sx={{ gap: '0.375rem', display: 'flex', flexFlow: 'wrap' }}>
                            {Object.entries(props.docsPerPerson[row.id] ?? {}).map(
                                ([document, num]) => {
                                    if(document !== 'Document chargé'){
                                        return(
                                            <QuadralBadge
                                                icon={getStatusIcon(document)}
                                                label={`${document} ${num}`}
                                                color={document === 'Validée' ? 'success'
                                                    : document === 'Refusée' ? 'error'
                                                    : document === 'A valider' ? 'info'
                                                    : document === 'A fournir' ? 'info'
                                                    : 'secondary'
                                            }/>
                                        )
                                    } 
                                    return(null);
                                }
                            )}
                        </QuadralContainer>
                    ),
                    email: row.email,
                    phone: row.phoneNumber,
                    person: row,
                }))
            )
        });
    }, [persons, props.docsPerPerson, getTaxSituation]);

    function getStatusIcon(status){
        switch (status) {
            case 'Validé' :
                return <FontAwesomeIcon icon={faCheck}/>
            case 'Refusé' :
                return <FontAwesomeIcon icon={faXmark}/>
            default :
                return <FontAwesomeIcon icon={faFileInvoice}/>
        };
    };

    const columns = [
        {
            id: "name",
            label: "NOM et Prénom",
            sortable: true,
            typography: "tableBodyWide"
        },
        {
            id: "occupantType",
            label: "Type",
        },
        {
            id: "taxSituation",
            label: "Situation fiscale",
            sortable: true,
        },
        {
            id: "birthdate",
            label: "Date de naissance",
            sortable: true,
        },
        {
            id: "familySituation",
            label: "Situation familiale",
            sortable: true,
        },
        {
            id: 'supportingDocuments',
            label: 'Pièces justificatives',
            sortable: true,
        },
        {
            id: "email",
            label: "Email",
            sortable: true,
        },
        {
            id: "phone",
            label: "Numéro de mobile",
        },
        {
            id: 'person',
            label: '',
            render: (row) => (
                <QuadralButton onClick={() => {setSelectedPerson(row.person); setOpen(true)}}>
                    Ouvrir
                </QuadralButton>
            ),
            toggleable: false,
        },
    ];

    return(<>
        <QuadralList
            title={<QuadralTypography variant="cardTitle" gutterBottom sx={{paddingLeft:'0.75rem'}}>Composition du foyer</QuadralTypography>}
            columns={columns}
            rows={rows}
            sortable
        />
        <UpdatePersonModal 
            selectedPerson={selectedPerson}
            open={open}
            setOpen={setOpen}
            showLoader={showLoader}
            hideLoader={hideLoader}
            participation={props.participation}
            refreshPersonsData={props.refreshPersonsData}
            references={references}
            mapReferenceToOptions={props.mapReferenceToOptions}
            quadralToast={props.quadralToast}
            isN3={isN3}
        />
        </>
    )
};

export default PersonList;